/**
 * Catalogue of node types available in the flow builder.
 *
 * `defaults()` returns the options payload a freshly dropped node starts with;
 * it must stay in sync with what the Ruby executors read
 * (app/services/flows/blocks/*.rb).
 *
 * User-facing copy lives in i18n keys; only operator-facing values (action and
 * condition names) stay inline, since they map 1:1 onto backend identifiers.
 */
export const NODE_TYPES = {
  trigger: {
    labelKey: 'FLOWS.NODES.TRIGGER.LABEL',
    descriptionKey: 'FLOWS.NODES.TRIGGER.DESCRIPTION',
    icon: 'i-lucide-zap',
    accent: 'blue',
    unique: true,
    defaults: () => ({
      inbox_ids: [],
      keywords: [],
      match_type: 'contains',
      initializeSession: 'always',
      listen_groups: false,
      receive_datasource: false,
      datasource_name: '',
      nextBlockId: '',
    }),
  },
  chat: {
    labelKey: 'FLOWS.NODES.CHAT.LABEL',
    descriptionKey: 'FLOWS.NODES.CHAT.DESCRIPTION',
    icon: 'i-lucide-message-circle',
    accent: 'blue',
    defaults: () => ({
      nextBlockId: '',
      messages: [
        {
          name: 'send-text-message',
          options: { text: '', buttons: [], breakMessages: false },
        },
      ],
    }),
  },
  action: {
    labelKey: 'FLOWS.NODES.ACTION.LABEL',
    descriptionKey: 'FLOWS.NODES.ACTION.DESCRIPTION',
    icon: 'i-lucide-zap',
    accent: 'amber',
    defaults: () => ({ nextBlockId: '', errorNextBlockId: '', actions: [] }),
  },
  condition: {
    labelKey: 'FLOWS.NODES.CONDITION.LABEL',
    descriptionKey: 'FLOWS.NODES.CONDITION.DESCRIPTION',
    icon: 'i-lucide-filter',
    accent: 'iris',
    defaults: () => ({
      trueNextBlockId: '',
      falseNextBlockId: '',
      conditions: [],
    }),
  },
  delay: {
    labelKey: 'FLOWS.NODES.DELAY.LABEL',
    descriptionKey: 'FLOWS.NODES.DELAY.DESCRIPTION',
    icon: 'i-lucide-clock',
    accent: 'violet',
    defaults: () => ({
      nextBlockId: '',
      delay: { name: 'minutes-delay', options: { minutes: 30 } },
    }),
  },
  randomizer: {
    labelKey: 'FLOWS.NODES.RANDOMIZER.LABEL',
    descriptionKey: 'FLOWS.NODES.RANDOMIZER.DESCRIPTION',
    icon: 'i-lucide-shuffle',
    accent: 'ruby',
    defaults: () => ({
      randomizers: [{ id: 'a', name: 'A', perc: 100, nextBlockId: '' }],
    }),
  },
  api: {
    labelKey: 'FLOWS.NODES.API.LABEL',
    descriptionKey: 'FLOWS.NODES.API.DESCRIPTION',
    icon: 'i-lucide-braces',
    accent: 'teal',
    defaults: () => ({
      nextBlockId: '',
      errorNextBlockId: '',
      apis: [
        {
          name: 'json-http-request-api',
          options: {
            url: '',
            method: 'GET',
            headers: [],
            query: [],
            body: '',
            datasourceName: 'Api-request-1',
            retryCount: 0,
            retryIntervalSeconds: 5,
          },
        },
      ],
    }),
  },
  'field-operation': {
    labelKey: 'FLOWS.NODES.FIELD_OPERATION.LABEL',
    descriptionKey: 'FLOWS.NODES.FIELD_OPERATION.DESCRIPTION',
    icon: 'i-lucide-list-checks',
    accent: 'slate',
    defaults: () => ({
      nextBlockId: '',
      errorNextBlockId: '',
      fieldOperations: [],
    }),
  },
  'ai-agent': {
    labelKey: 'FLOWS.NODES.AI_AGENT.LABEL',
    descriptionKey: 'FLOWS.NODES.AI_AGENT.DESCRIPTION',
    icon: 'i-lucide-bot',
    accent: 'violet',
    defaults: () => ({
      endpoint: '',
      auth_token: '',
      timeoutInSeconds: 1800,
      maxFollowups: 2,
      timeoutNextBlockId: '',
      errorNextBlockId: '',
      action_routes: {},
    }),
  },
};

// Order shown in the "add node" palette.
export const PALETTE = [
  'chat',
  'action',
  'condition',
  'delay',
  'randomizer',
  'api',
  'field-operation',
  'ai-agent',
];

export const DELAY_UNITS = [
  { value: 'seconds-delay', labelKey: 'FLOWS.UNITS.SECONDS', key: 'seconds' },
  { value: 'minutes-delay', labelKey: 'FLOWS.UNITS.MINUTES', key: 'minutes' },
  { value: 'hours-delay', labelKey: 'FLOWS.UNITS.HOURS', key: 'hours' },
  { value: 'days-delay', labelKey: 'FLOWS.UNITS.DAYS', key: 'days' },
  { value: 'until-date-delay', labelKey: 'FLOWS.UNITS.UNTIL_DATE', key: 'date' },
  { value: 'time-interval-delay', labelKey: 'FLOWS.UNITS.TIME_INTERVAL', key: 'daysOfWeek' },
];

// Mirrors Flows::Blocks::Action::SUPPORTED.
export const ACTION_TYPES = [
  // --- Etiquetas ---
  { value: 'add_label', label: 'Adicionar etiqueta', fields: ['labels'], group: 'etiqueta' },
  { value: 'remove_label', label: 'Remover etiqueta', fields: ['labels'], group: 'etiqueta' },

  // --- Atendente / Time ---
  { value: 'assign_agent', label: 'Atribuir agente', fields: ['agent_id'], group: 'atendimento' },
  { value: 'assign_team', label: 'Atribuir time', fields: ['team_id'], group: 'atendimento' },
  { value: 'add_attendant_on_lead_action', label: 'Atribuir agente ao lead', fields: ['agent_id'], group: 'atendimento' },
  { value: 'clean_attendant_on_lead_action', label: 'Remover agente do lead', fields: [], group: 'atendimento' },

  // --- Conversa ---
  { value: 'change_status', label: 'Alterar status', fields: ['status'], group: 'conversa' },
  { value: 'change_priority', label: 'Alterar prioridade', fields: ['priority'], group: 'conversa' },
  { value: 'resolve_conversation', label: 'Resolver conversa', fields: [], group: 'conversa' },
  { value: 'open_conversation', label: 'Reabrir conversa', fields: [], group: 'conversa' },
  { value: 'start_conversation_action', label: 'Iniciar conversa', fields: [], group: 'conversa' },
  { value: 'finish_conversation', label: 'Finalizar atendimento', fields: [], group: 'conversa' },
  { value: 'finish_conversation_action', label: 'Encerrar conversa', fields: [], group: 'conversa' },
  { value: 'snooze_conversation', label: 'Adiar conversa', fields: [], group: 'conversa' },
  { value: 'mute_conversation', label: 'Silenciar conversa', fields: [], group: 'conversa' },

  // --- Notas ---
  { value: 'add_private_note', label: 'Nota privada', fields: ['content'], group: 'nota' },
  { value: 'add_lead_comment_action', label: 'Comentário no lead', fields: ['content'], group: 'nota' },

  // --- Contato ---
  { value: 'set_contact_attribute', label: 'Definir campo do contato', fields: ['key', 'value'], group: 'contato' },
  { value: 'create_lead_action', label: 'Criar lead/contato', fields: [], group: 'contato' },
  { value: 'delete_lead_action', label: 'Limpar dados do contato', fields: [], group: 'contato' },

  // --- Negócio / Deal ---
  { value: 'create_business_action', label: 'Criar negócio', fields: ['stageId'], group: 'negocio' },
  { value: 'move_business_action', label: 'Mover negócio de etapa', fields: ['stageId'], group: 'negocio' },
  { value: 'lose_business_action', label: 'Perder negócio', fields: ['content'], group: 'negocio' },
  { value: 'win_business_action', label: 'Ganhar negócio', fields: [], group: 'negocio' },
  { value: 'add_attendant_on_business_action', label: 'Atribuir agente ao negócio', fields: ['agent_id'], group: 'negocio' },
  { value: 'clean_attendant_on_business_action', label: 'Remover agente do negócio', fields: [], group: 'negocio' },
  { value: 'clone_business_action', label: 'Clonar negócio', fields: ['stageId'], group: 'negocio' },
  { value: 'restore_business_action', label: 'Restaurar negócio', fields: [], group: 'negocio' },
  { value: 'add_product_on_business_action', label: 'Adicionar produto ao negócio', fields: ['productSku', 'quantity', 'price'], group: 'negocio' },
  { value: 'remove_product_on_business_action', label: 'Remover produto do negócio', fields: ['productSku', 'quantity'], group: 'negocio' },

  // --- Produto ---
  { value: 'create_product_action', label: 'Criar produto', fields: ['name', 'sku', 'price'], group: 'produto' },

  // --- Sistema ---
  { value: 'send_notification_action', label: 'Enviar notificação', fields: ['notification', 'url'], group: 'sistema' },
  { value: 'start_another_automation_action', label: 'Iniciar outra automação', fields: ['automationId'], group: 'sistema' },

  // --- Automações Chat ---
  { value: 'start_chat_automations_action', label: 'Iniciar automações de chat', fields: [], group: 'automacao' },
  { value: 'stop_chat_automations_action', label: 'Parar automações de chat', fields: [], group: 'automacao' },

  // --- Etiquetas (extras) ---
  { value: 'create_tag_action', label: 'Criar etiqueta', fields: ['name', 'color'], group: 'etiqueta' },
];

// Mirrors the branches of Flows::Blocks::Condition#matches?.
export const CONDITION_TYPES = [
  // --- Campos ---
  { value: 'field-is-equal-condition', label: 'é igual a', valueKey: 'equalsTo' },
  { value: 'field-not-equal-condition', label: 'é diferente de', valueKey: 'equalsTo' },
  { value: 'field-contains-condition', label: 'contém', valueKey: 'contains' },
  { value: 'field-greater-than-condition', label: 'é maior que', valueKey: 'value' },
  { value: 'field-less-than-condition', label: 'é menor que', valueKey: 'value' },
  { value: 'field-is-empty-condition', label: 'está vazio', valueKey: null },
  { value: 'field-is-present-condition', label: 'está preenchido', valueKey: null },

  // --- Contato ---
  { value: 'lead-exists-condition', label: 'contato existe', valueKey: null, noParameter: true },
  { value: 'lead-has-label-condition', label: 'tem etiqueta', valueKey: 'labels', noParameter: true },
  { value: 'contact-has-email-condition', label: 'contato tem email', valueKey: null, noParameter: true },
  { value: 'contact-has-phone-condition', label: 'contato tem telefone', valueKey: null, noParameter: true },
  { value: 'contact-has-name-condition', label: 'contato tem nome', valueKey: null, noParameter: true },
  { value: 'lead-with-taxid-exists-condition', label: 'contato tem CPF', valueKey: null, noParameter: true },
  { value: 'lead-has-attendant-condition', label: 'lead tem atendente', valueKey: null, noParameter: true },

  // --- Conversa ---
  { value: 'conversation-status-condition', label: 'status da conversa é', valueKey: 'status', noParameter: true },
  { value: 'conversation-has-agent-condition', label: 'conversa tem atendente', valueKey: null, noParameter: true },
  { value: 'conversation-has-team-condition', label: 'conversa tem time', valueKey: null, noParameter: true },
  { value: 'conversation-has-finalized-condition', label: 'conversa foi finalizada', valueKey: null, noParameter: true },
  { value: 'conversation-window-is-open-condition', label: 'janela 24h aberta', valueKey: null, noParameter: true },

  // --- Negócio / Deal ---
  { value: 'lead-has-business-on-pipeline-condition', label: 'lead tem negócio no pipeline', valueKey: 'pipelineId', noParameter: true },
  { value: 'lead-has-business-on-stage-condition', label: 'lead tem negócio na etapa', valueKey: 'stageId', noParameter: true },
  { value: 'business-is-won-condition', label: 'negócio ganho', valueKey: null, noParameter: true },
  { value: 'business-is-lost-condition', label: 'negócio perdido', valueKey: null, noParameter: true },
  { value: 'business-is-pending-condition', label: 'negócio pendente', valueKey: null, noParameter: true },
  { value: 'business-has-attendants-condition', label: 'negócio tem atendente', valueKey: null, noParameter: true },

  // --- Tempo ---
  { value: 'current-time-interval-condition', label: 'horário atual no intervalo', valueKey: null, noParameter: true },

  // --- Campos (extras) ---
  { value: 'between-condition', label: 'está entre (intervalo)', valueKey: 'greaterOrEqualsTo' },
  { value: 'field-has-value-condition', label: 'possui valor', valueKey: null },

  // --- Contato (extras) ---
  { value: 'lead-has-additional-fields-condition', label: 'lead tem campo adicional', valueKey: 'additionalFieldId', noParameter: true },
  { value: 'lead-with-additional-field-exists-condition', label: 'campo adicional do lead igual a', valueKey: 'value', noParameter: true },

  // --- Negócio (extras) ---
  { value: 'business-attendant-empty-condition', label: 'negócio sem atendente', valueKey: null, noParameter: true },
  { value: 'business-with-external-id-exists-condition', label: 'negócio com ID externo', valueKey: 'externalId', noParameter: true },
  { value: 'business-with-additional-field-exists-condition', label: 'campo adicional do negócio igual a', valueKey: 'value', noParameter: true },
  { value: 'business-has-product-condition', label: 'negócio tem produto', valueKey: null, noParameter: true },

  // --- Conversa (extras) ---
  { value: 'conversation-has-department-condition', label: 'conversa tem departamento', valueKey: 'departmentId', noParameter: true },
];

export const CONDITION_CATEGORIES = [
  {
    id: 'contact',
    label: 'Contato / Lead',
    description: 'Condições sobre o contato/lead',
    icon: 'user',
    conditions: [
      { value: 'lead-exists-condition', title: 'Contato existente', description: 'Verifica se o contato já está cadastrado' },
      { value: 'contact-has-email-condition', title: 'Contato com email', description: 'Verifica se o contato possui email cadastrado' },
      { value: 'contact-has-phone-condition', title: 'Contato com telefone', description: 'Verifica se o contato possui telefone cadastrado' },
      { value: 'contact-has-name-condition', title: 'Contato com nome', description: 'Verifica se o contato possui nome cadastrado' },
      { value: 'lead-with-taxid-exists-condition', title: 'Contato com CPF', description: 'Verifica se o contato possui CPF cadastrado' },
      { value: 'lead-has-label-condition', title: 'Possui etiqueta', description: 'Verifica se a conversa possui uma das etiquetas informadas' },
      { value: 'lead-has-attendant-condition', title: 'Lead tem atendente', description: 'Verifica se o lead tem um atendente atribuído' },
      { value: 'lead-has-additional-fields-condition', title: 'Lead tem campo adicional', description: 'Verifica se o lead possui um campo adicional específico preenchido' },
      { value: 'lead-with-additional-field-exists-condition', title: 'Campo adicional do lead igual a', description: 'Verifica se um campo adicional do lead tem um valor específico' },
    ],
  },
  {
    id: 'conversation',
    label: 'Conversa',
    description: 'Condições sobre a conversa',
    icon: 'message-circle',
    conditions: [
      { value: 'conversation-status-condition', title: 'Status da conversa', description: 'Verifica se a conversa possui um status específico' },
      { value: 'conversation-has-agent-condition', title: 'Conversa com atendente', description: 'Verifica se a conversa possui atendente responsável' },
      { value: 'conversation-has-team-condition', title: 'Conversa em time', description: 'Verifica se a conversa está atribuída a um time' },
      { value: 'conversation-has-finalized-condition', title: 'Conversa finalizada', description: 'Verifica se a conversa foi resolvida/finalizada' },
      { value: 'conversation-window-is-open-condition', title: 'Janela 24h aberta', description: 'Verifica se a janela de 24h do WhatsApp está ativa' },
      { value: 'conversation-has-department-condition', title: 'Conversa tem departamento', description: 'Verifica se a conversa está atribuída a um departamento específico' },
    ],
  },
  {
    id: 'fields',
    label: 'Campos',
    description: 'Condições de campos do sistema ou campos adicionais',
    icon: 'text-cursor-input',
    conditions: [
      { value: 'field-is-equal-condition', title: 'Campo com valor igual', description: 'Verifica se um campo possui um valor exatamente igual' },
      { value: 'field-not-equal-condition', title: 'Campo com valor diferente', description: 'Verifica se um campo possui um valor diferente' },
      { value: 'field-contains-condition', title: 'Campo contém valor', description: 'Verifica se um campo contém um texto' },
      { value: 'field-is-present-condition', title: 'Campo possui valor', description: 'Verifica se um campo está preenchido' },
      { value: 'field-is-empty-condition', title: 'Campo está vazio', description: 'Verifica se um campo está sem valor' },
      { value: 'field-greater-than-condition', title: 'Campo maior que', description: 'Comparação numérica: maior que' },
      { value: 'field-less-than-condition', title: 'Campo menor que', description: 'Comparação numérica: menor que' },
      { value: 'between-condition', title: 'Campo entre valores', description: 'Verifica se o valor está entre dois números' },
      { value: 'field-has-value-condition', title: 'Campo possui valor', description: 'Verifica se um campo possui qualquer valor definido' },
    ],
  },
  {
    id: 'business',
    label: 'Negócio / Deal',
    description: 'Condições sobre negócios/deals no pipeline',
    icon: 'briefcase',
    conditions: [
      { value: 'lead-has-business-on-pipeline-condition', title: 'Lead tem negócio no pipeline', description: 'Verifica se o lead tem negócio ativo em um pipeline específico' },
      { value: 'lead-has-business-on-stage-condition', title: 'Lead tem negócio na etapa', description: 'Verifica se o lead tem negócio em uma etapa específica' },
      { value: 'business-is-won-condition', title: 'Negócio ganho', description: 'Verifica se o negócio atual foi ganho' },
      { value: 'business-is-lost-condition', title: 'Negócio perdido', description: 'Verifica se o negócio atual foi perdido' },
      { value: 'business-is-pending-condition', title: 'Negócio pendente', description: 'Verifica se o negócio atual está pendente' },
      { value: 'business-has-attendants-condition', title: 'Negócio tem atendente', description: 'Verifica se o negócio tem um atendente atribuído' },
      { value: 'business-attendant-empty-condition', title: 'Negócio sem atendente', description: 'Verifica se o negócio não possui atendente atribuído' },
      { value: 'business-with-external-id-exists-condition', title: 'Negócio com ID externo', description: 'Verifica se existe um negócio com o ID externo informado' },
      { value: 'business-with-additional-field-exists-condition', title: 'Campo adicional do negócio', description: 'Verifica se um campo adicional do negócio tem valor específico' },
      { value: 'business-has-product-condition', title: 'Negócio tem produto', description: 'Verifica se o negócio possui produtos adicionados' },
    ],
  },
  {
    id: 'time',
    label: 'Tempo',
    description: 'Condições baseadas em horário',
    icon: 'clock',
    conditions: [
      { value: 'current-time-interval-condition', title: 'Horário no intervalo', description: 'Verifica se o horário atual está dentro de um intervalo' },
    ],
  },
];

export const CONVERSATION_STATUSES = ['open', 'resolved', 'pending', 'snoozed'];
export const PRIORITIES = ['', 'urgent', 'high', 'medium', 'low'];
export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// Chat message sub-types
export const CHAT_MESSAGE_TYPES = [
  { value: 'send-text-message', label: 'Enviar texto' },
  { value: 'text-input-message', label: 'Aguardar resposta' },
  { value: 'send-file-message', label: 'Enviar arquivo' },
  { value: 'send-url-file-message', label: 'Enviar arquivo por URL' },
  { value: 'send-audio-message', label: 'Enviar áudio' },
  { value: 'delay-message', label: 'Simular digitando...' },
];

// Variables offered by the `{...}` shortcut buttons in text fields.
export const BUILTIN_VARIABLES = [
  { token: '{Nome do contato|leadName}', labelKey: 'FLOWS.VARIABLES.NAME' },
  {
    token: '{Primeiro nome|leadFirstName}',
    labelKey: 'FLOWS.VARIABLES.FIRST_NAME',
  },
  { token: '{Sobrenome|leadLastName}', labelKey: 'FLOWS.VARIABLES.LAST_NAME' },
  { token: '{Email|leadEmail}', labelKey: 'FLOWS.VARIABLES.EMAIL' },
  { token: '{Telefone|leadPhone}', labelKey: 'FLOWS.VARIABLES.PHONE' },
  { token: '{CPF|leadTaxId}', labelKey: 'FLOWS.VARIABLES.TAX_ID' },
  {
    token: '{Última resposta|lastInput}',
    labelKey: 'FLOWS.VARIABLES.LAST_INPUT',
  },
];

// Sample values for technical fields (HTTP header names, JSON bodies). These are
// syntax examples, not copy, so they are never translated.
export const PLACEHOLDERS = {
  headerKey: 'Authorization',
  headerValue: 'Bearer ...',
  queryKey: 'job_id',
  queryValue: '{id|[Api-request-1]job_id}',
  body: '{ "cpf": "{cpf|leadTaxId}" }',
  branch: 'A',
  percent: '%',
};
